import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {AutocompleteItem, FetchSuggestions} from './AutocompleteTokenTagInput'
import styles from './AutocompleteTokenTagInput.module.css'

function colorLeadingVisual(color: string): React.FC {
  const bg = color.startsWith('#') ? color : `#${color}`
  const ColorCircle = () => <span className={styles.colorCircle} style={{backgroundColor: bg}} />
  ColorCircle.displayName = 'ColorCircle'
  return ColorCircle
}

export function createLabelFetcher(owner: string, name: string): FetchSuggestions {
  const repo = `${owner}/${name}`
  return async (query, signal) => {
    const url = `/_filter/labels?q=${encodeURIComponent(query)}&repo=${encodeURIComponent(repo)}`
    const response = await verifiedFetchJSON(url, {signal})
    if (!response.ok) return []
    const data = await response.json()
    return (data.labels ?? []).map(
      (l: {name: string; color?: string; description?: string}): AutocompleteItem => ({
        id: l.name,
        text: l.name,
        color: l.color,
        description: l.description,
        leadingVisual: l.color ? colorLeadingVisual(l.color) : undefined,
      }),
    )
  }
}

export function createIssueTypeFetcher(owner: string, name: string): FetchSuggestions {
  return async (query, signal) => {
    const url = `/_ghui/item-pickers/issue_types?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(name)}`
    const response = await verifiedFetchJSON(url, {signal})
    if (!response.ok) return []
    const data = await response.json()
    const edges: Array<{node: {id: string; name: string; color?: string; description?: string; isEnabled?: boolean}}> =
      data?.data?.repository?.issueTypes?.edges ?? []
    return edges
      .filter(e => e.node.isEnabled !== false)
      .filter(e => e.node.name.toLowerCase().includes(query.toLowerCase()))
      .map(
        (e): AutocompleteItem => ({
          id: e.node.name,
          text: e.node.name,
          description: e.node.description,
        }),
      )
  }
}

export function createIssueFieldFetcher(owner: string, name: string): FetchSuggestions {
  const repo = `${owner}/${name}`
  return async (query, signal) => {
    const url = `/_filter/issue_fields?q=${encodeURIComponent(query)}&repo=${encodeURIComponent(repo)}`
    const response = await verifiedFetchJSON(url, {signal})
    if (!response.ok) return []
    const data = await response.json()
    return (data.issue_fields ?? []).map(
      (f: {name: string; data_type?: string; description?: string}): AutocompleteItem => ({
        id: f.name,
        text: f.name,
        description: f.description,
      }),
    )
  }
}
